import React from "react";
import { BillingMethod, Plan } from "services/quotes/types";
import { moneyWithComma } from "utils/form/helpers";
import { NoDataLine } from "components/common/structures/index";
import { unitByAnnualUsage } from "utils/data";
import { AnnualUsageUnit } from "services/orders/types";
import { planAnnualUsageString } from "utils/services";
import { humanFormatEffectiveDate } from "utils/retail/plans";
import { css } from "@emotion/css";
import useTheme from "evcharging/hooks/useTheme";
import { Theme } from "evcharging/app/themes";
import RatesTooltip from "containers/retail/sections/energy-rates-3/RatesTooltip";
import useI18n from "evcharging/hooks/useI18n";
import { isPlanShortDetails } from "config/retail";
import { getGreenEnergyImage } from "../../../../config/suppliers";

interface PlanDetailsProps {
  plan: Plan;
}

const RatesPlanDetails: React.FC<PlanDetailsProps> = (props) => {
  const { t } = useI18n();

  const theme = useTheme();
  const classes = createStyles(theme);

  const { plan } = props;
  const totalAnnualUsage = planAnnualUsageString(plan);
  const isCCF = plan.annualUsage.unit === AnnualUsageUnit.CCF;
  const unit = unitByAnnualUsage(plan.annualUsage);
  const isShortDetails = isPlanShortDetails(plan);

  return (
    <ul className={classes.listWrapper}>
      <li className={classes.listItem}>
        <span className={classes.infoTitle}>{t("rates.price-per-unit", { unit })}</span>
        <span className={classes.infoValue}>¢{moneyWithComma(plan.price.amount * 100, 3)}</span>
      </li>
      <li className={classes.listItem}>
        <span className={classes.infoTitle}>{t("rates.term")}</span>
        <span className={classes.infoValue}>{t("rates.n-months", { n: plan.term.value })}</span>
      </li>
      <li className={classes.listItem}>
        <span className={classes.infoTitle}>{t("rates.contract-start-date")}</span>
        <span className={classes.infoValue}>
            {humanFormatEffectiveDate(new Date(plan.effectiveDate))}
          </span>
      </li>
      <li className={classes.listItem}>
        <span className={classes.infoTitle}>
          {t("rates.billing-method")}
          <RatesTooltip>
            {t("rates.ucb")}
          </RatesTooltip>
        </span>
        <span className={classes.infoValue}>
          {BillingMethod[plan.billingMethod] || <NoDataLine />}
        </span>
      </li>

      {isShortDetails || (
        <>
          <li className={classes.listItem}>
            <span className={classes.infoTitle}>
              {t("rates.load-factor")}
              <RatesTooltip>
                {t("rates.load-factor-tooltip")}
              </RatesTooltip>
            </span>
            <span className={classes.infoValue}>{plan.loadFactor}%</span>
          </li>
          <li className={classes.listItem}>
            <span className={classes.infoTitle}>
              {t("rates.bandwidth")}
              <RatesTooltip>
                {t("rates.bandwidth-tooltip")}
              </RatesTooltip>
            </span>
            <span className={classes.infoValue}>{plan.bandwidth}%</span>
          </li>
        </>
      )}

      {plan.cancellationFee && (
        <li className={classes.listItem}>
          <span className={classes.infoTitle}>{t("rates.cancellation-fee")}</span>
          <span className={classes.infoValue}>{plan.cancellationFee}</span>
        </li>
      )}

      {isCCF ? (
        <li className={classes.listItem}>
          <span className={classes.infoTitle}>
            {t("rates.annual-usage")}
            <RatesTooltip>
              {t("rates.annual-usage-tooltip")}
            </RatesTooltip>
          </span>
          <span className={classes.infoValue}>{totalAnnualUsage}</span>
        </li>
      ) : (
        <li className={classes.listItem}>
          <span className={classes.infoTitle}>{t("rates.annual-usage")}</span>
          <span className={classes.infoValue}>{totalAnnualUsage}</span>
        </li>
      )}
      {plan.energySource && (
        <li className={classes.listItem}>
          <span className={classes.infoTitle}>{"Green energy"}</span>
          <span className={classes.infoValue}>
            <img src={getGreenEnergyImage(plan)} alt={""} height={24} width={24} />
          </span>
        </li>
      )}
    </ul>
  );
};

const createStyles = (theme: Theme) => ({
  listWrapper: css({
    margin: 0,
    padding: theme.spacing(2.5),
    "& $listItem": {
      "&:last-child": {
        borderBottom: "none"
      }
    }
  }),
  listItem: css({
    display: "flex",
    justifyContent: "space-between",
    padding: theme.spacing(1.25, 0),
    borderBottom: "1px solid #E8E8E8",
    fontSize: theme.spacing(1.75),
    color: "#646464",
    fontWeight: 600
  }),
  infoTitle: css({
    position: "relative",
    minWidth: 80,
    display: "flex",
    alignItems: "center"
  }),
  infoValue: css({
    display: "flex",
    alignItems: "center",
    textAlign: "right",
    paddingLeft: theme.spacing(2),
    color: "black"
  }),
  infoIcon: css({
    display: "block",
    marginTop: 5,
    marginLeft: 5,
    cursor: "pointer"
  }),
  tooltip: css({
    position: "absolute",
    bottom: "100%",
    left: 0,
    background: "#000",
    color: "#fff",
    width: 250,
    padding: 8,
    borderRadius: 4
  })
});

export default RatesPlanDetails;
